import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import {
  ASSET_PATHWAY_STATUSES, ASSET_PATHWAY_LABELS, ASSET_PATHWAY_DESCRIPTIONS,
  AssetPathwayBadge,
} from "@/components/AssetPathwayBadge";

// ---------------------------------------------------------------------------
// MP12 Module 3 §E — Asset Pathway change dialog.
//
// Authorised roles: super_admin, catalog_manager. Enforced by the backend
// endpoint; the UI simply exposes it. A written reason is REQUIRED for
// any change into or out of DEMO_ONLY, and for any elevation to
// CLIENT_READY_RELEASE_ELIGIBLE. The dialog surfaces the exact rules that
// govern each pathway so the operator makes an informed choice.
// ---------------------------------------------------------------------------

export function AssetPathwayDialog({ open, onOpenChange, track, onChanged }) {
  const current = (track?.asset_pathway || "").trim();
  const [target, setTarget] = useState(current || "DEVELOPMENT_CANDIDATE");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTarget(current || "DEVELOPMENT_CANDIDATE");
      setReason("");
    }
  }, [open, current]);

  const reasonRequired = (
    current === "DEMO_ONLY" || target === "DEMO_ONLY" || target === "CLIENT_READY_RELEASE_ELIGIBLE"
  );

  async function submit() {
    if (!track?.id) return;
    if (target === current) {
      toast.error("Choose a different pathway before saving.");
      return;
    }
    if (reasonRequired && !reason.trim()) {
      toast.error("A written reason is required for this change.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/tracks/${track.id}/asset-pathway`, {
        asset_pathway: target,
        reason: reason.trim(),
      });
      toast.success(`Asset Pathway set: ${ASSET_PATHWAY_LABELS[target] || target}`);
      onChanged && onChanged(r.data);
      onOpenChange && onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change Asset Pathway.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange && onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="asset-pathway-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
            Change Asset Pathway
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            The pathway is authoritative for downstream gating. Verified records are never overwritten by this change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-[12.5px]">
            <span className="text-muted-foreground mr-2">Current:</span>
            <AssetPathwayBadge value={current} />
          </div>

          <RadioGroup value={target} onValueChange={setTarget} data-testid="asset-pathway-target">
            {ASSET_PATHWAY_STATUSES.map((code) => (
              <label key={code} className="flex items-start gap-2 border rounded p-2.5 cursor-pointer hover:bg-muted/40" data-testid={`asset-pathway-option-${code}`}>
                <RadioGroupItem value={code} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <AssetPathwayBadge value={code} />
                    {code === current && (
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">(current)</span>
                    )}
                  </div>
                  <p className="text-[11.5px] text-muted-foreground mt-1">
                    {ASSET_PATHWAY_DESCRIPTIONS[code]}
                  </p>
                </div>
              </label>
            ))}
          </RadioGroup>

          <div>
            <Label className="text-[11px] uppercase tracking-wide">
              Reason {reasonRequired && <span className="text-[hsl(0_65%_38%)]">(required)</span>}
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why is this pathway being changed?"
              className="mt-1"
              data-testid="asset-pathway-reason"
            />
          </div>

          <div
            className="rounded border p-2.5 flex items-start gap-2"
            style={{ background: "hsl(40 55% 96%)", borderColor: "hsl(40 55% 78%)" }}
          >
            <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: "hsl(35 60% 30%)" }} />
            <p className="text-[11px] leading-snug text-muted-foreground">
              The pathway label alone is not clearance. Downstream endpoints will still require underlying readiness (documents, clearance status, attorney flags) to be satisfied.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange && onOpenChange(false)} disabled={busy} data-testid="asset-pathway-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || target === current || (reasonRequired && !reason.trim())}
            style={{ background: "hsl(347 72% 22%)" }}
            data-testid="asset-pathway-save"
          >
            {busy ? "Saving…" : "Change pathway"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssetPathwayDialog;
